import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MessageSquare, FolderKanban, Plus, MoreHorizontal, Pencil, FolderInput, FolderMinus,
  Inbox, Pin, PinOff, Archive, Trash2, Copy, ArchiveRestore, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CONVERSATION_DRAG_TYPE, onConversationDrop, moveConversation, renameConversation, requestNewChat,
  pinConversation, archiveConversation, deleteConversation, reorderPins, useWorkspaceProjects,
  useWorkspaceConversations, useArchived,
} from "@/lib/workspaceProjects";
import { EditableName } from "@/components/workspace/RailEditableName";

const iconBtn = "w-6 h-6 rounded-md flex items-center justify-center text-[var(--qn-foreground-subtle)] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[var(--qn-surface-hover)]";

export default function RailChats({ collapsed }) {
  const navigate = useNavigate();
  const { area, sub } = useParams();
  const { conversations } = useWorkspaceConversations();
  const { projects } = useWorkspaceProjects();
  const [editingId, setEditingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { chats: archivedChats } = useArchived(showArchived);
  if (collapsed) return null;

  const known = projects.filter((p) => p.project_id);
  const pinned = conversations.filter((c) => c.pinned)
    .sort((a, b) => (a.pin_order ?? 9e15) - (b.pin_order ?? 9e15));
  const rest = conversations.filter((c) => !c.pinned);
  const byProject = new Map();
  rest.forEach((c) => {
    const k = c.project_id || "__none";
    if (!byProject.has(k)) byProject.set(k, []);
    byProject.get(k).push(c);
  });

  const move = async (c, projectId, name) => {
    try { await moveConversation(c.conversation_id, projectId); toast.success(projectId ? `Moved to “${name}”` : "Removed from project"); }
    catch { toast.error("Could not move the chat"); }
  };
  const commitRename = async (c, title) => {
    setEditingId(null);
    try { await renameConversation(c.conversation_id, title); toast.success("Chat renamed"); }
    catch { toast.error("Could not rename the chat"); }
  };
  const togglePin = async (c) => {
    try { await pinConversation(c.conversation_id, !c.pinned); toast.success(c.pinned ? "Unpinned" : "Pinned"); }
    catch { toast.error("Could not update the chat"); }
  };
  const setArchived = async (c, val) => {
    try { await archiveConversation(c.conversation_id, val); toast.success(val ? "Chat archived" : "Chat unarchived"); }
    catch { toast.error("Could not update the chat"); }
  };
  const copyId = async (c) => {
    try { await navigator.clipboard.writeText(c.conversation_id); toast.success("Conversation ID copied"); }
    catch { toast.error("Could not copy"); }
  };
  const confirmDelete = async () => {
    const c = pendingDelete;
    setPendingDelete(null);
    if (!c) return;
    try { await deleteConversation(c.conversation_id); toast.success("Chat deleted"); }
    catch { toast.error("Could not delete the chat"); }
  };
  const reorder = async (draggedId, targetId) => {
    const ids = pinned.map((c) => c.conversation_id);
    if (!ids.includes(draggedId) || draggedId === targetId) return;
    ids.splice(ids.indexOf(draggedId), 1);
    const to = ids.indexOf(targetId);
    ids.splice(to < 0 ? ids.length : to, 0, draggedId);
    try { await reorderPins(ids); } catch { toast.error("Could not reorder pins"); }
  };

  const chatRow = (c, reorderable = false) => {
    const active = area === "chat" && sub === c.conversation_id;
    const editing = editingId === c.conversation_id;
    const reorderProps = reorderable ? {
      onDragOver: (e) => { if ([...e.dataTransfer.types].includes(CONVERSATION_DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } },
      onDrop: (e) => { const id = e.dataTransfer.getData(CONVERSATION_DRAG_TYPE); if (id) { e.preventDefault(); e.stopPropagation(); reorder(id, c.conversation_id); } },
    } : {};
    return (
      <div key={c.conversation_id} {...reorderProps} className="group flex items-center gap-1" data-testid={`rail-chat-row-${c.conversation_id}`}>
        {editing ? (
          <EditableName value={c.title || "Untitled"} onCommit={(t) => commitRename(c, t)} onCancel={() => setEditingId(null)} />
        ) : (
          <button data-testid={`rail-chat-${c.conversation_id}`}
            draggable
            onDragStart={(e) => { e.dataTransfer.setData(CONVERSATION_DRAG_TYPE, c.conversation_id); e.dataTransfer.effectAllowed = "move"; }}
            onClick={() => navigate(`/app/chat/${c.conversation_id}`)}
            onDoubleClick={() => setEditingId(c.conversation_id)}
            title={c.title || "Untitled"}
            className={`flex-1 min-w-0 text-left px-3 py-1.5 rounded-md text-xs truncate flex items-center gap-2 cursor-grab active:cursor-grabbing transition-colors ${
              active ? "text-[var(--qn-primary)] bg-[var(--qn-primary-muted)]" : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"}`}>
            {c.pinned ? <Pin className="w-3 h-3 shrink-0 text-[var(--qn-primary)]" /> : <MessageSquare className="w-3.5 h-3.5 shrink-0" />}
            <span className="truncate">{c.title || "Untitled"}</span>
          </button>
        )}
        {!editing && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`rail-chat-menu-${c.conversation_id}`} aria-label="Chat options" className={iconBtn}>
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem data-testid={`rail-chat-rename-${c.conversation_id}`} onClick={() => setEditingId(c.conversation_id)} className="text-xs">
                <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`rail-chat-copyid-${c.conversation_id}`} onClick={() => copyId(c)} className="text-xs">
                <Copy className="w-3.5 h-3.5 mr-2" /> Copy conversation ID
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`rail-chat-pin-${c.conversation_id}`} onClick={() => togglePin(c)} className="text-xs">
                {c.pinned ? <PinOff className="w-3.5 h-3.5 mr-2" /> : <Pin className="w-3.5 h-3.5 mr-2" />} {c.pinned ? "Unpin" : "Pin"}
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="text-xs"><FolderInput className="w-3.5 h-3.5 mr-2" /> Change project</DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-52">
                  {known.filter((p) => p.project_id !== c.project_id).map((p) => (
                    <DropdownMenuItem key={p.project_id} onClick={() => move(c, p.project_id, p.name)}
                      data-testid={`rail-chat-move-${c.conversation_id}-to-${p.project_id}`} className="text-xs">
                      <FolderKanban className="w-3.5 h-3.5 mr-2" /> {p.name}
                    </DropdownMenuItem>
                  ))}
                  {!known.filter((p) => p.project_id !== c.project_id).length && (
                    <div className="px-2 py-1.5 text-[11px] text-[var(--qn-foreground-subtle)]">No other projects.</div>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              {c.project_id && (
                <DropdownMenuItem data-testid={`rail-chat-ungroup-${c.conversation_id}`} onClick={() => move(c, null)} className="text-xs">
                  <FolderMinus className="w-3.5 h-3.5 mr-2" /> Remove from project
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`rail-chat-archive-${c.conversation_id}`} onClick={() => setArchived(c, true)} className="text-xs">
                <Archive className="w-3.5 h-3.5 mr-2" /> Archive
              </DropdownMenuItem>
              <DropdownMenuItem data-testid={`rail-chat-delete-${c.conversation_id}`} onClick={() => setPendingDelete(c)} className="text-xs text-red-500 focus:text-red-500">
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  };

  const group = (key, label, Icon, chats, dropProjectId, dropMsg, reorderable = false) => (
    <div key={key} data-testid={`rail-chat-group-${key}`}
      {...(dropProjectId !== undefined ? onConversationDrop(dropProjectId, (ok) => toast[ok ? "success" : "error"](ok ? dropMsg : "Could not move the chat")) : {})}
      className="rounded-md border border-dashed border-transparent hover:border-[var(--qn-border)] transition-colors">
      <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-[0.14em] text-[var(--qn-foreground-subtle)] font-semibold flex items-center gap-1.5">
        <Icon className="w-3 h-3" /> <span className="truncate">{label}</span>
        <span className="ml-auto font-mono normal-case tracking-normal">{chats.length}</span>
      </div>
      <div className="space-y-0.5">
        {chats.map((c) => chatRow(c, reorderable))}
        {!chats.length && dropProjectId !== undefined && (
          <div className="px-3 pb-1 text-[11px] text-[var(--qn-foreground-subtle)]">{dropProjectId === null ? "Drop a chat here to remove it from its project" : "Drop a chat here"}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="pl-6 pr-1 pb-1 space-y-2" data-testid="rail-chats">
      <button data-testid="rail-new-chat" onClick={() => { navigate("/app/chat"); requestNewChat(); }}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-[var(--qn-primary)] hover:bg-[var(--qn-primary-muted)] transition-colors">
        <Plus className="w-3.5 h-3.5" /> New chat
      </button>

      {pinned.length > 0 && group("pinned", "Pinned", Pin, pinned, undefined, undefined, true)}
      {group("ungrouped", "Ungrouped", Inbox, byProject.get("__none") || [], null, "Removed from project")}
      {known.map((p) => group(p.project_id, p.name, FolderKanban, byProject.get(p.project_id) || [], p.project_id, `Moved to “${p.name}”`))}

      {/* Archived toggle */}
      <button data-testid="rail-archived-toggle" onClick={() => setShowArchived((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--qn-foreground-subtle)] hover:bg-[var(--qn-surface-hover)]">
        <ChevronRight className={`w-3 h-3 transition-transform ${showArchived ? "rotate-90" : ""}`} /> <Archive className="w-3 h-3" /> Archived
      </button>
      {showArchived && (
        <div data-testid="rail-archived-section" className="space-y-0.5">
          {archivedChats.length === 0 && <div className="px-3 pb-1 text-[11px] text-[var(--qn-foreground-subtle)]">No archived chats</div>}
          {archivedChats.map((c) => (
            <div key={c.conversation_id} className="group flex items-center gap-1" data-testid={`rail-archived-chat-${c.conversation_id}`}>
              <span className="flex-1 min-w-0 px-3 py-1.5 text-xs truncate text-[var(--qn-foreground-subtle)] flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{c.title || "Untitled"}</span>
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`rail-archived-chat-menu-${c.conversation_id}`} aria-label="Archived chat options" className={iconBtn}>
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem data-testid={`rail-archived-chat-unarchive-${c.conversation_id}`} onClick={() => setArchived(c, false)} className="text-xs">
                    <ArchiveRestore className="w-3.5 h-3.5 mr-2" /> Unarchive
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`rail-archived-chat-delete-${c.conversation_id}`} onClick={() => setPendingDelete(c)} className="text-xs text-red-500 focus:text-red-500">
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="rail-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this chat?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.title || "Untitled"}” will be removed from your workspace. This can be recovered by an administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rail-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rail-delete-confirm" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
